import { jsx } from "panel";
import { SelectTheme } from "common/components/select/constants";
import { SelectEl } from "common/components/select";
import { ItemsMenuEl } from "common/components/items-menu";
import { ButtonBarEl } from "common/components/button-bar";
import { SkeletonListRowSize } from "common/components/skeleton-list/types";
import { SkeletonListEl } from "common/components/skeleton-list";
import { Placement } from "common/components/tooltip/types";
import { ButtonSize, ButtonTheme } from "common/components/button/constants";
import { ResourceType } from "common/report/constants";
import { ButtonEl } from "common/components/button";
import { renameEvent } from "common/report/util/events";
import { ItemsMenuSize, SelectedTheme } from "common/components/items-menu/types";
import { Direction } from "common/components/resizable-panel/types";
import { ResizablePanelEl } from "common/components/resizable-panel";
import { MenuSearchBar } from "../mixins/search";
import { FilterDropdownWrapperEl } from "../filter-dropdown-wrapper";
import { AllTab, SortBy, StagedItemType } from "./types";
export function template() {
  var _a;
  const useNewLayout = this.canUseImprovedQbMenus();
  const stagedBackHandler = this.hasStagedItem() && this.helpers.unstageProperty || this.backButtonHandler;
  if (!this.attr(`open`)) {
    return /* @__PURE__ */ jsx("div", {
      sel: ".mp-properties-menu",
      class: { "mp-child-menu": true },
      hook: { insert: (vnode) => this.helpers.setChildMenuRef(vnode) }
    });
  }
  const { searchInputEl } = this.state;
  const createSectionItems = this.helpers.getCreateSectionItems();
  const CreateItemsMenuEl = ({ forNewLayout = false } = {}) => {
    const hasAddFilterButton = this.helpers.useMultipleSelection();
    return createSectionItems.length > 0 ? createSectionItems.length === 1 ? /* @__PURE__ */ jsx(ButtonEl, {
      sel: ".create-custom-button",
      attrs: {
        "show-preview-tooltip": false,
        theme: forNewLayout ? ButtonTheme.Tertiary : ButtonTheme.MenuFooter,
        "hide-input-focus-styling": true,
        icon: forNewLayout ? `plus` : ``,
        ...forNewLayout && {
          tooltip: { text: `Create Custom ${createSectionItems[0].label}`, placement: Placement.Top }
        }
      },
      on: {
        click: () => this.showCreateItemModal(createSectionItems[0].value)
      }
    }, !forNewLayout && /* @__PURE__ */ jsx("div", {
      sel: ".create-custom-label",
      attrs: { elref: `label` }
    }, "Create Custom ", createSectionItems[0].label)) : /* @__PURE__ */ jsx(SelectEl, {
      sel: ".create-custom-button",
      attrs: {
        "disable-search": true,
        "show-preview-tooltip": false,
        sections: [{ items: createSectionItems }],
        theme: forNewLayout ? SelectTheme.Tertiary : SelectTheme.MenuFooter,
        "icon-only": forNewLayout,
        icon: forNewLayout ? { leftIcon: `plus` } : null,
        size: forNewLayout ? ButtonSize.Medium : ButtonSize.Inline,
        label: forNewLayout ? `` : `Create Custom...`,
        "hide-input-focus-styling": true,
        "menu-width": 240,
        "no-rounded-corners": hasAddFilterButton && !useNewLayout,
        ...forNewLayout && {
          tooltip: { text: `Create Custom Property or Cohort`, placement: Placement.Top }
        }
      },
      on: { change: this.helpers.handleCreateItemClick }
    }) : null;
  };
  const RefreshButton = () => /* @__PURE__ */ jsx(ButtonEl, {
    attrs: {
      elref: `refreshButton`,
      icon: `refresh`,
      disabled: this.state.isLoading,
      theme: ButtonTheme.Tertiary,
      tooltip: { text: `Refresh properties`, placement: Placement.Left }
    },
    on: {
      click: this.helpers.handleRefreshButtonClick
    }
  });
  const InnerSearch = /* @__PURE__ */ jsx(MenuSearchBar, {
    value: this.state.searchFilter,
    handleSearchInput: this.helpers.handleSearchInput,
    backButtonClick: useNewLayout ? null : stagedBackHandler,
    onInsert: (el) => this.update({ searchInputEl: el }),
    key: `search`
  }, useNewLayout && /* @__PURE__ */ jsx(CreateItemsMenuEl, {
    forNewLayout: true
  }));
  const showLoadingSkeleton = this.state.isLoading;
  const FilterSelectEl = () => /* @__PURE__ */ jsx(FilterDropdownWrapperEl, {
    attrs: {
      "always-show-caret": true,
      theme: SelectTheme.MenuFilter,
      size: ButtonSize.Inline,
      sections: [{ label: `Filter by`, items: this.helpers.getFilterOptionSectionItems() }],
      "menu-width": null,
      "empty-label": `all properties`
    },
    props: {
      handleFilterChange: (ev) => this.helpers.handleFilterChange(ev.detail.item.value)
    }
  });
  const SortSelectEl = () => /* @__PURE__ */ jsx(SelectEl, {
    attrs: {
      "icon-only": true,
      icon: {
        leftIcon: (() => {
          switch (this.state.sortByValue) {
            case SortBy.NameAsc:
              return `sort-alpha-asc`;
            case SortBy.NameDesc:
              return `sort-alpha-desc`;
            default:
              return `relevancy`;
          }
        })()
      },
      theme: SelectTheme.Tertiary,
      size: ButtonSize.Medium,
      sections: [{ label: `Sort By`, items: this.helpers.getSortingOptionSectionItems() }],
      "menu-width": null,
      tooltip: { placement: Placement.Left }
    },
    on: {
      change: this.helpers.handleSortChange
    }
  });
  const SortFilterRowEl = () => {
    let showing;
    let action = this.state.searchFilter ? `Searching ` : `Show`;
    let target = ``;
    switch (this.state.selectedTab) {
      case AllTab:
      case ResourceType.Cohort:
        showing = /* @__PURE__ */ jsx(FilterSelectEl, null);
        break;
      case `events`:
        showing = /* @__PURE__ */ jsx("div", null, `events`);
        break;
      default:
        return null;
    }
    if (this.state.stagedItemType && !this.isUnsortableStage()) {
      switch (this.state.stagedItemType) {
        case StagedItemType.BehaviorMultiAttribution:
          action = `Attributed by...`;
          break;
        case StagedItemType.BehaviorWithEvent:
          action = `Select Event...`;
          break;
        case StagedItemType.EventWithProperties:
          action = `Select Property on `;
          target = `${renameEvent(this.state.stagedItemsStack[this.state.stagedItemsStack.length - 1].value.value)}...`;
          break;
      }
      showing = ``;
    }
    if (this.attr(`property-of-label`)) {
      action = `Select Property on `;
      target = `${this.attr(`property-of-label`)}...`;
      showing = null;
    }
    return !this.isUnsortableStage() ? /* @__PURE__ */ jsx("div", {
      sel: ".header-filter-row"
    }, /* @__PURE__ */ jsx("div", {
      sel: ".showing-text-wrapper"
    }, /* @__PURE__ */ jsx("div", {
      sel: ".showing-text"
    }, action, target && /* @__PURE__ */ jsx("b", null, target)), showing), /* @__PURE__ */ jsx("div", {
      sel: ".header-extra-controls"
    }, /* @__PURE__ */ jsx(RefreshButton, null), /* @__PURE__ */ jsx(SortSelectEl, null))) : /* @__PURE__ */ jsx("div", {
      sel: ".empty-header-filter-row"
    });
  };
  const LoadingContent = /* @__PURE__ */ jsx(SkeletonListEl, {
    attrs: { "num-rows": 12, "row-size": SkeletonListRowSize.Large },
    sel: ".mp-properties-menu-skeleton-list"
  });
  const MenuContent = /* @__PURE__ */ jsx(null, null, !useNewLayout && InnerSearch, /* @__PURE__ */ jsx("div", {
    sel: ".mp-properties-menu-header"
  }, !useNewLayout && /* @__PURE__ */ jsx(SortFilterRowEl, null), /* @__PURE__ */ jsx("slot", {
    attrs: { name: `menu-header` }
  })), showLoadingSkeleton ? LoadingContent : /* @__PURE__ */ jsx(ItemsMenuEl, {
    sel: ".mp-properties-menu-items-menu",
    key: [
      this.hasStagedItem() ? `stageditem:${this.peekStagedItem().label}` : `tab:${this.state.selectedTab}`
    ].join(`-`),
    attrs: {
      elref: `mainSelectionMenu`,
      "empty-message": `No items found`,
      "search-filter": this.getSearchFilter(),
      multiple: this.helpers.useMultipleSelection(),
      "select-all-mechanism": this.attr(`select-all-mechanism`),
      "show-select-all": this.attr(`show-select-all`),
      "select-all-disabled": this.isSelectAllDisabled(),
      "project-is-free-plan": this.attr(`project-is-free-plan`),
      "disable-search-sort": true,
      "disable-search-filter-matching-parts": useNewLayout === false,
      "disable-first-item-padding": useNewLayout
    },
    on: {
      click: (ev) => ev.stopPropagation(),
      clickExpanderItem: this.handleExpandItemClick.bind(this),
      clickItem: this.helpers.handleClickItem,
      clickPill: this.helpers.onItemPillClick,
      selectAll: this.helpers.handleSelectAll,
      tooltipContextAction: this.helpers.handleTooltipContextAction,
      tooltipContextEditDescription: this.helpers.handleTooltipContextEditDescription
    },
    props: {
      sections: this.helpers.getMenuSections(),
      keydownProxyEl: !this.state.isModalOpen && searchInputEl,
      sectionsFromInputFn: this.helpers.sectionsFromInput,
      customSearchMatcherFn: this.helpers.customSearchMatcherFn,
      contextPanelTransform: this.helpers.contextPanelTransform,
      contextPanelPositionParentElement: this.helpers.getChildMenuRef()
    }
  }));
  const ApplyButton = this.shouldShowApplyButton() && (useNewLayout ? /* @__PURE__ */ jsx("div", {
    sel: ".mp-properties-menu-footer"
  }, ((_a = this.getArraySelection()) == null ? void 0 : _a.length) > 0 && /* @__PURE__ */ jsx("div", {
    sel: ".mp-properties-menu-footer-selection"
  }, this.getArraySelection().length, " Selected", /* @__PURE__ */ jsx(ButtonEl, {
    attrs: {
      role: `button`,
      elref: `clearSelection`,
      theme: ButtonTheme.Tertiary,
      size: ButtonSize.Medium
    },
    on: {
      click: () => this.clearArraySelection()
    }
  }, "Clear")), /* @__PURE__ */ jsx(ButtonEl, {
    attrs: {
      role: `button`,
      elref: `applyButton`,
      theme: ButtonTheme.Primary,
      disabled: this.isApplyButtonDisabled()
    },
    on: {
      click: this.helpers.onApplyButtonClick
    }
  }, this.attr(`apply-button-text`) || `Select`)) : /* @__PURE__ */ jsx(ButtonBarEl, {
    sel: ".mp-button-bar-primary",
    attrs: {
      role: `button`,
      elref: `applyButton`,
      disabled: this.isApplyButtonDisabled()
    },
    on: {
      click: this.helpers.onApplyButtonClick
    }
  }, this.getApplyButtonText()));
  const sidenavItems = this.helpers.getFilterOptionSectionItemsForSidenav();
  return /* @__PURE__ */ jsx("div", {
    sel: ".mp-properties-menu",
    class: { "beta-menu": useNewLayout, "mp-child-menu": true, "is-loading": showLoadingSkeleton },
    hook: { insert: (vnode) => this.helpers.setChildMenuRef(vnode) }
  }, useNewLayout && InnerSearch, /* @__PURE__ */ jsx("div", {
    sel: ".mp-properties-menu-wrapper"
  }, useNewLayout && (sidenavItems == null ? void 0 : sidenavItems.length) > 0 && /* @__PURE__ */ jsx(ResizablePanelEl, {
    sel: ".mp-properties-menu-sidenav-panel",
    attrs: {
      "storage-key": `properties-menu-sidebar`,
      "default-size": 120,
      "min-size": 120,
      "max-size": 300,
      "disable-collapsing": true,
      "panel-name": `Properties Menu Sidebar`,
      "resize-direction": Direction.Right
    },
    props: { containerEl: this.parentElement }
  }, this.state.isLoadingDataGroups ? LoadingContent : /* @__PURE__ */ jsx(ItemsMenuEl, {
    attrs: {
      tabindex: 0,
      size: ItemsMenuSize.Small,
      "disable-first-item-padding": true
    },
    props: {
      sections: [{ items: sidenavItems, selectedTheme: SelectedTheme.Focus }]
    },
    on: {
      clickItem: (ev) => this.helpers.handleFilterChange(ev.detail.item.value)
    }
  })), /* @__PURE__ */ jsx("div", {
    sel: ".mp-properties-menu-content"
  }, MenuContent, !useNewLayout && /* @__PURE__ */ jsx(CreateItemsMenuEl, {
    forNewLayout: false
  }), ApplyButton)));
}
